import React, { useState, useRef } from "react";
import {
    Box,
    Typography,
    Paper,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    Radio,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { HttpUserAddress, HttpCache } from "../../http";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { EntityWithIdAndDescription } from "../../types/baseEntities";



import { BUTTON_STYLES } from "../../styles/buttonStyles";
import { TYPOGRAPHY_STYLES } from "../../styles/typographyStyles";
import { alpha } from "@mui/material/styles";
import { STYLES } from "../ShoppingCart/ShoppingCartStyles";
import { useUserAddresses } from "../../contexts/UserDataContext";



const TEXT = {
    pageTitle: {
        ...TYPOGRAPHY_STYLES.rubik,
        fontSize: 24,
        fontWeight: 700,
    },
    cardTitle: {
        ...TYPOGRAPHY_STYLES.rubikLarge,
        fontWeight: 700,
    },
    meta: {
        ...TYPOGRAPHY_STYLES.rubikSmall,
        opacity: 0.8,
    },
    value: {
        ...TYPOGRAPHY_STYLES.rubikMedium,
        fontWeight: 600,
    },
} as const;

const ICON_SX = {
    ...BUTTON_STYLES.icon,
    height: 32,
    width: 32,
} as const;

const ICON_DANGER_SX = {
    ...ICON_SX,
    color: "error.main",
    "&:hover": { backgroundColor: "transparent", color: "error.dark" },
} as const;

const DANGER_BUTTON_SX = {
    ...BUTTON_STYLES.invertedPrimary,
    width: "auto",
    minWidth: "fit-content",
    backgroundColor: "error.main",
    "&:before": { display: "none" },
    "&:hover": {
        backgroundColor: "error.dark",
        color: "white",
    },
} as const;

const FIELD_SX = {
    "& .MuiInputLabel-root": {
        ...TYPOGRAPHY_STYLES.rubikSmall,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.3px",
    },
    "& .MuiOutlinedInput-root": {
        borderRadius: 0,
    },
    "& .MuiInputBase-input": {
        ...TYPOGRAPHY_STYLES.rubikMedium,
        fontWeight: 600,
    },
    "& .MuiSelect-select": {
        ...TYPOGRAPHY_STYLES.rubikMedium,
        fontWeight: 600,
    },
    "& .MuiFormHelperText-root": {
        ...TYPOGRAPHY_STYLES.rubikSmall,
        marginLeft: 0,
    },
} as const;

const schema = yup.object({
    calle: yup.string().required("La calle es obligatoria"),
    calleNro: yup.string().required("El número es obligatorio"),
    deptoPiso: yup.string().nullable(),
    barrio: yup.string().nullable(),
    codigoPostal: yup.string().required("El código postal es obligatorio"),

    codProvincia: yup
        .number()
        .typeError("Debe seleccionar una provincia")
        .moreThan(0, "Debe seleccionar una provincia"),

    codLocalidad: yup
        .number()
        .typeError("Debe seleccionar una localidad")
        .moreThan(0, "Debe seleccionar una localidad"),
});


export const ProfileAddressesSection = () => {
    const { addresses, active, refresh } = useUserAddresses();

    const [editingId, setEditingId] = useState<number | null>(null);
    const [deleteId, setDeleteId] = useState<number | null>(null);

    const [provincias, setProvincias] = useState<EntityWithIdAndDescription[]>([]);
    const [localidades, setLocalidades] = useState<EntityWithIdAndDescription[]>([]);
    const [loadingLocalidades, setLoadingLocalidades] = useState(false);

    const [selectId, setSelectId] = useState<number | null>(null);

    const [isCreating, setIsCreating] = useState(false);

    const { handleSubmit, control, reset, watch, setValue } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            calle: "",
            calleNro: "",
            deptoPiso: "",
            barrio: "",
            codigoPostal: "",
            codProvincia: 0,
            codLocalidad: 0,
        },
    });

    const selectedProvince = watch("codProvincia");

    const loadProvinces = async () => {
        const prov = await HttpCache.getProvincies();
        setProvincias(prov);
    };

    const citiesRequestIdRef = useRef(0);

    const loadCities = async (provId: number) => {
        if (!provId || provId <= 0) return;

        const requestId = ++citiesRequestIdRef.current;

        setLoadingLocalidades(true);
        setLocalidades([]);

        const loc = await HttpCache.getCitiesByProvince(provId);

        if (requestId !== citiesRequestIdRef.current) return;

        setLocalidades(loc);
        setLoadingLocalidades(false);
    };




    const startCreating = async () => {
        await loadProvinces();
        setLocalidades([]);

        reset({
            calle: "",
            calleNro: "",
            deptoPiso: "",
            barrio: "",
            codigoPostal: "",
            codProvincia: 0,
            codLocalidad: 0,
        });

        setIsCreating(true);
        setEditingId(null);
    };

    const submitCreate = async (data: any) => {
        const resp = await HttpUserAddress.createAddress({
            ...data,
            codPais: 1,
        });

        if (resp.tieneError) {
            alert(resp.descripcionError);
            return;
        }

        setIsCreating(false);
        refresh();
    };

    const confirmSelection = async () => {
        if (!selectId) return;

        try {
            const resp = await HttpUserAddress.selectAddress(selectId);
            if ((resp as any).tieneError) {
                alert((resp as any).descripcionError);
                return;
            }

            await refresh();
            setSelectId(null);
        } catch (e: any) {
            alert(e?.message ?? "Error al seleccionar dirección");
        }
    };

    const startEditing = async (address: any) => {
        await loadProvinces();
        await loadCities(address.codProvincia);

        reset({
            calle: address.calle ?? "",
            calleNro: address.calleNro ?? "",
            deptoPiso: address.deptoPiso ?? "",
            barrio: address.barrio ?? "",
            codigoPostal: address.codigoPostal ?? "",
            codProvincia: address.codProvincia ?? 0,
            codLocalidad: address.codLocalidad ?? 0,
        });

        setEditingId(address.id);
        setIsCreating(false);
    };

    const submitEdit = async (data: any) => {
        if (!editingId) return;

        const resp = await HttpUserAddress.updateAddress(editingId, {
            ...data,
            codPais: 1,
        });

        if (resp.tieneError) {
            alert(resp.descripcionError);
            return;
        }

        setEditingId(null);
        refresh();
    };

    const deleteAddress = async () => {
        if (!deleteId) return;

        const resp = await HttpUserAddress.deleteAddress(deleteId);

        if (resp.tieneError) {
            alert(resp.descripcionError);
            return;
        }

        setDeleteId(null);
        refresh();
    };

    return (
        <Box>
            <Typography sx={{ ...TEXT.pageTitle, mb: 3 }}>Direcciones guardadas</Typography>

            {addresses.length === 0 ? (
                <Typography sx={TEXT.value}>No tenés direcciones cargadas.</Typography>
            ) : (
                addresses.map((a) => (
                    <Paper
                        key={a.id}
                        onClick={() => {
                            if (editingId === null && !isCreating) setSelectId(a.id);
                        }}
                        sx={() => ({
                            p: 2,
                            mb: 2,
                            position: "relative",
                            overflow: "hidden",
                            borderRadius: 2,

                            border: "1px solid",
                            borderColor: a.seleccionado ? STYLES.colors.secondary : "#ddd",

                            backgroundColor: a.seleccionado
                                ? alpha(STYLES.colors.secondary, 0.06)
                                : "transparent",

                            cursor: editingId || isCreating ? "default" : "pointer",
                            transition: "0.2s",

                            "&:hover": {
                                borderColor: editingId || isCreating ? undefined : STYLES.colors.secondary,
                                backgroundColor:
                                    editingId || isCreating
                                        ? undefined
                                        : alpha(STYLES.colors.secondary, 0.05),
                            },

                            "&::after": {
                                content: '""',
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: 5,
                                height: "100%",
                                backgroundColor: STYLES.colors.secondary,
                                opacity: a.seleccionado ? 1 : 0,
                                transition: "opacity 0.2s ease",
                            },
                        })}

                    >
                        {editingId === a.id ? (
                            <Box component="form" onSubmit={handleSubmit(submitEdit)}>
                                <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                                    <Controller
                                        name="calle"
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <TextField
                                                {...field}
                                                label="Calle"
                                                fullWidth
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                                sx={FIELD_SX}
                                            />
                                        )}
                                    />

                                    <Controller
                                        name="calleNro"
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <TextField
                                                {...field}
                                                label="Número"
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                                sx={{ ...FIELD_SX, width: 140 }}
                                            />
                                        )}
                                    />
                                </Box>

                                {/* Piso/Depto */}
                                <Controller
                                    name="deptoPiso"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField {...field} label="Piso/Depto" fullWidth sx={{ ...FIELD_SX, mb: 2 }} />
                                    )}
                                />

                                <Controller
                                    name="codigoPostal"
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            {...field}
                                            label="Código Postal"
                                            fullWidth
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                            sx={{ ...FIELD_SX, mb: 2 }}
                                        />
                                    )}
                                />

                                {/* Provincia */}
                                <Controller
                                    name="codProvincia"
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            select
                                            label="Provincia"
                                            fullWidth
                                            sx={{ ...FIELD_SX, mb: 2 }}
                                            value={field.value ?? 0}
                                            onChange={async (e) => {
                                                const provId = Number(e.target.value);

                                                // actualizo provincia
                                                field.onChange(provId);

                                                // reseteo localidad SOLO cuando el usuario cambia provincia
                                                setValue("codLocalidad", 0, { shouldValidate: true });
                                                setLocalidades([]);

                                                // cargo localidades
                                                if (provId > 0) {
                                                    await loadCities(provId);
                                                }
                                            }}
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                        >
                                            <MenuItem value={0}>Seleccionar provincia</MenuItem>
                                            {provincias.map((p: any) => (
                                                <MenuItem key={p.id} value={p.id}>
                                                    {p.descripcion}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    )}
                                />



                                {/* Localidad */}
                                <Controller
                                    name="codLocalidad"
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            select
                                            fullWidth
                                            label="Localidad"
                                            disabled={loadingLocalidades || !selectedProvince || selectedProvince <= 0}
                                            sx={{ ...FIELD_SX, mb: 2 }}
                                            value={field.value ?? 0}
                                            onChange={(e) => field.onChange(Number(e.target.value))}
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                        >
                                            <MenuItem value={0}>
                                                {loadingLocalidades ? "Cargando..." : "Seleccionar localidad"}
                                            </MenuItem>

                                            {localidades.map((l: any) => (
                                                <MenuItem key={l.id} value={l.id}>
                                                    {l.descripcion}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    )}
                                />


                                {/* Barrio */}
                                <Controller
                                    name="barrio"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField {...field} label="Barrio" fullWidth sx={{ ...FIELD_SX, mb: 2 }} />
                                    )}
                                />

                                <Box display="flex" gap={2}>
                                    <Button
                                        variant="contained"
                                        type="submit"
                                        sx={{ ...BUTTON_STYLES.invertedPrimary, width: "auto", minWidth: "fit-content" }}
                                    >
                                        Guardar
                                    </Button>

                                    <Button
                                        variant="outlined"
                                        onClick={() => setEditingId(null)}
                                        sx={{ ...BUTTON_STYLES.outlinedPrimary, width: "auto", minWidth: "fit-content" }}
                                    >
                                        Cancelar
                                    </Button>
                                </Box>
                            </Box>
                        ) : (
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                                    <Radio
                                        checked={!!a.seleccionado}
                                        size="small"
                                        disableRipple
                                        sx={{
                                            mt: 0.25,
                                            p: 0,
                                            color: alpha(STYLES.colors.secondary, 0.45),
                                            "&.Mui-checked": {
                                                color: STYLES.colors.secondary,
                                            },
                                            "& .MuiSvgIcon-root": {
                                                fontSize: 18,
                                            },
                                        }}
                                    />


                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={TEXT.cardTitle}>
                                        {a.calle} {a.calleNro} {a.deptoPiso ? `(${a.deptoPiso})` : ""}
                                    </Typography>

                                    <Typography sx={TEXT.meta}>
                                        {a.codigoPostal} - {a.barrio}, {a.descLocalidad}, {a.descProvincia}, {a.descPais}
                                    </Typography>
                                </Box>

                                <Box sx={{ display: "flex", gap: 1, alignSelf: "center" }}>
                                    <IconButton
                                        sx={ICON_SX}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            startEditing(a);
                                        }}
                                    >
                                        <EditIcon />
                                    </IconButton>

                                    <IconButton
                                        sx={ICON_DANGER_SX}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setDeleteId(a.id);
                                        }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            </Box>
                        )}
                    </Paper>

                ))
            )}

            {!isCreating ? (
                <Paper
                    onClick={() => startCreating()}
                    sx={{
                        p: 3,
                        mt: 3,
                        cursor: "pointer",
                        border: "1px dashed #999",
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        transition: "0.2s",
                        "&:hover": {
                            borderColor: "#000",
                            backgroundColor: "#fafafa",
                        },
                    }}
                >
                    <Typography sx={{ ...TEXT.value, fontWeight: 700, fontSize: "15px" }}>
                        + Agregar nueva dirección
                    </Typography>
                </Paper>
            ) : (
                <Paper sx={{ p: 2, mb: 2, mt: 3, border: "1px dashed #999", borderRadius: 2 }}>
                    <Box component="form" onSubmit={handleSubmit(submitCreate)}>
                        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                            <Controller
                                name="calle"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <TextField
                                        {...field}
                                        label="Calle"
                                        fullWidth
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        sx={FIELD_SX}
                                    />
                                )}
                            />

                            <Controller
                                name="calleNro"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <TextField
                                        {...field}
                                        label="Número"
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        sx={{ ...FIELD_SX, width: 140 }}
                                    />
                                )}
                            />
                        </Box>

                        <Controller
                            name="deptoPiso"
                            control={control}
                            render={({ field }) => (
                                <TextField {...field} label="Piso/Depto" fullWidth sx={{ ...FIELD_SX, mb: 2 }} />
                            )}
                        />

                        <Controller
                            name="codigoPostal"
                            control={control}
                            render={({ field, fieldState }) => (
                                <TextField
                                    {...field}
                                    label="Código Postal"
                                    fullWidth
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                    sx={{ ...FIELD_SX, mb: 2 }}
                                />
                            )}
                        />

                        {/* Provincia */}
                        <Controller
                            name="codProvincia"
                            control={control}
                            render={({ field, fieldState }) => (
                                <TextField
                                    select
                                    label="Provincia"
                                    fullWidth
                                    sx={{ ...FIELD_SX, mb: 2 }}
                                    value={field.value ?? 0}
                                    onChange={async (e) => {
                                        const provId = Number(e.target.value);

                                        // actualizo provincia
                                        field.onChange(provId);

                                        // reseteo localidad SOLO cuando el usuario cambia provincia
                                        setValue("codLocalidad", 0, { shouldValidate: true });
                                        setLocalidades([]);

                                        // cargo localidades
                                        if (provId > 0) {
                                            await loadCities(provId);
                                        }
                                    }}
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                >
                                    <MenuItem value={0}>Seleccionar provincia</MenuItem>
                                    {provincias.map((p: any) => (
                                        <MenuItem key={p.id} value={p.id}>
                                            {p.descripcion}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            )}
                        />



                        {/* Localidad */}
                        <Controller
                            name="codLocalidad"
                            control={control}
                            render={({ field, fieldState }) => (
                                <TextField
                                    select
                                    fullWidth
                                    label="Localidad"
                                    disabled={loadingLocalidades || !selectedProvince || selectedProvince <= 0}
                                    sx={{ ...FIELD_SX, mb: 2 }}
                                    value={field.value ?? 0}
                                    onChange={(e) => field.onChange(Number(e.target.value))}
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                >
                                    <MenuItem value={0}>
                                        {loadingLocalidades ? "Cargando..." : "Seleccionar localidad"}
                                    </MenuItem>

                                    {localidades.map((l: any) => (
                                        <MenuItem key={l.id} value={l.id}>
                                            {l.descripcion}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            )}
                        />


                        {/* Barrio */}
                        <Controller
                            name="barrio"
                            control={control}
                            render={({ field }) => (
                                <TextField {...field} label="Barrio" fullWidth sx={{ ...FIELD_SX, mb: 2 }} />
                            )}
                        />

                        {/* Botones */}
                        <Box display="flex" gap={2}>
                            <Button
                                variant="contained"
                                type="submit"
                                sx={{ ...BUTTON_STYLES.invertedPrimary, width: "auto", minWidth: "fit-content" }}
                            >
                                Guardar
                            </Button>

                            <Button
                                variant="outlined"
                                onClick={() => setIsCreating(false)}
                                sx={{ ...BUTTON_STYLES.outlinedPrimary, width: "auto", minWidth: "fit-content" }}
                            >
                                Cancelar
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            )}

            <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
                <DialogTitle>
                    <Typography sx={TEXT.cardTitle}>Eliminar dirección</Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography sx={TEXT.value}>
                        ¿Estás seguro que querés eliminar la dirección?
                        <br />
                        Esta acción no se puede deshacer.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "center", gap: 2, px: 3, pb: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={() => setDeleteId(null)}
                        sx={{ ...BUTTON_STYLES.outlinedPrimary, width: "auto", minWidth: "fit-content" }}
                    >
                        Cancelar
                    </Button>
                    <Button variant="contained" onClick={deleteAddress} sx={DANGER_BUTTON_SX}>
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* === DIALOG ACTIVA === */}
            <Dialog open={!!selectId} onClose={() => setSelectId(null)}>
                <DialogTitle>
                    <Typography sx={TEXT.cardTitle}>Cambiar dirección seleccionada</Typography>
                </DialogTitle>

                <DialogContent>
                    <Typography sx={TEXT.value}>
                        Vas a cambiar tu dirección activa.
                        <br />
                        Esta acción puede afectar el costo y opciones de envío.
                    </Typography>
                </DialogContent>

                <DialogActions sx={{ justifyContent: "center", gap: 2, px: 3, pb: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={() => setSelectId(null)}
                        sx={{ ...BUTTON_STYLES.outlinedPrimary, width: "auto", minWidth: "fit-content" }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={confirmSelection}
                        sx={{ ...BUTTON_STYLES.primary, width: "auto", minWidth: "fit-content" }}
                    >
                        Cambiar dirección
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};














